import { commonApi } from '@common/utils/baseQuery';
import type {
  AnswerSurveyStepArgs,
  OpenSurveySessionArgs,
  SurveyAvailableItem,
  SurveySession,
  SurveyStep,
  SurveyTrigger,
  SurveyTriggerCode,
} from '@features/survey/api/surveyApi.type';

const toTrigger = (response: unknown): SurveyTrigger | null =>
  response && typeof response === 'object' && 'surveyId' in response ? response as SurveyTrigger : null;

export const surveyApi = commonApi
  .enhanceEndpoints({ addTagTypes: [ 'SurveyAvailable' ] })
  .injectEndpoints({
    endpoints: (builder) => ({
      fetchAvailableSurveys: builder.query<SurveyAvailableItem[], number>({
        query: () => '/survey/available',
        transformResponse: (response: unknown) => (Array.isArray(response) ? response as SurveyAvailableItem[] : []),
        providesTags: [ 'SurveyAvailable' ],
      }),
      fetchSurveyBanner: builder.query<SurveyTrigger | null, number>({
        query: () => '/survey/triggers?code=cabinet_banner',
        transformResponse: toTrigger,
        providesTags: [ 'SurveyAvailable' ],
        keepUnusedDataFor: 60,
      }),
      checkSurveyTrigger: builder.mutation<SurveyTrigger | null, SurveyTriggerCode>({
        query: (code) => ({ url: `/survey/triggers?code=${encodeURIComponent(code)}`, method: 'GET' }),
        transformResponse: toTrigger,
      }),
      openSurveySession: builder.mutation<SurveySession, OpenSurveySessionArgs>({
        query: ({ surveyId, triggerCode, campaignId }) => ({
          url: `/survey/${surveyId}/session`,
          method: 'POST',
          body: { triggerCode, campaignId },
        }),
      }),
      answerSurveyStep: builder.mutation<SurveyStep, AnswerSurveyStepArgs>({
        query: ({ token, ...body }) => ({ url: `/survey/session/${token}/answer`, method: 'POST', body }),
      }),
      completeSurveySession: builder.mutation<SurveyStep, { token: string }>({
        query: ({ token }) => ({ url: `/survey/session/${token}/complete`, method: 'POST', body: {} }),
      }),
    }),
    overrideExisting: false,
  });

export const {
  useFetchAvailableSurveysQuery,
  useFetchSurveyBannerQuery,
  useCheckSurveyTriggerMutation,
  useOpenSurveySessionMutation,
  useAnswerSurveyStepMutation,
  useCompleteSurveySessionMutation,
} = surveyApi;
